import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@libs/zapi-ui/shadcn";
import { AppButton, Typography, ConfirmationModal } from "@libs/zapi-ui/custom";
import { Link, useNavigate } from "react-router-dom";
import { PenIcon, Trash } from "lucide-react";
import { useState } from "react";
import { TProject, useProjectStore } from "@/zapi-frontend/stores/project";
import { getRouteEditProject, getRouteProjectDetails } from "@/zapi-frontend/shared/const/router";
import { AxiosError } from "axios";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { GET_PROJECTS_LIST_KEY } from "@/zapi-frontend/shared/services/useGetProjectsList";

const colors = ["#9b57f5", "#f0a161", "#6153f4"];

type TProjectsTableProps = { tableData: TProject[] };

export const ProjectsTable = ({ tableData }: TProjectsTableProps) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState<string | null>(null);
  const { archiveProject } = useProjectStore();

  const handleArchiveProject = async (id: string) => {
    const [, archiveError] = await tryCatch<void, AxiosError<{ message: string }>>(archiveProject(id));
    if (archiveError) {
      toast.error(archiveError.response?.data.message || "Failed to delete project");
      return;
    }

    toast.success("Project deleted successfully");
    queryClient.invalidateQueries({ queryKey: [GET_PROJECTS_LIST_KEY] });
  };

  return (
    <Table className="table-fixed w-full">
      <TableHeader className={"p-[12px] text-zinc-500 text-[10px]"}>
        <TableRow>
          <TableHead className="w-[calc(100%-200px)]">Name</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {tableData.map((project, index) => {
          const projectRoute = getRouteProjectDetails(project.id);

          const handleRowClick = (e: React.MouseEvent) => {
            // Don't navigate if clicking on action buttons
            if ((e.target as HTMLElement).closest("button") || (e.target as HTMLElement).closest("a[data-testid]")) {
              return;
            }
            navigate(projectRoute);
          };

          return (
            <TableRow key={project.id} className="cursor-pointer" onClick={handleRowClick}>
              <TableCell className="overflow-hidden">
                <div className="flex items-center gap-2">
                  <div
                    className="w-10 h-10 rounded-md flex items-center justify-center overflow-hidden text-white text-ellipsis font-sans text-[26px] font-medium leading-[40px] tracking-[-0.26px]"
                    style={{ backgroundColor: colors[index % colors.length] }}
                  >
                    {project.name.slice(0, 1).toUpperCase()}
                  </div>

                  <div className="flex-1 min-w-0 overflow-hidden">
                    <Typography variant="label2" className="block truncate leading-normal">
                      {project.name}
                    </Typography>
                    <Typography variant="body3" color="zinc600" className="block truncate font-light">
                      {project.shortDescription || "-"}
                    </Typography>
                  </div>
                </div>
              </TableCell>
              <TableCell className="text-right whitespace-nowrap">
                <Link
                  data-testid={`projects-table-edit-project-${project.id}`}
                  to={getRouteEditProject(project.id)}
                  state={{ from: "projects" }}
                >
                  <AppButton appearance="text" size="icon">
                    <PenIcon />
                  </AppButton>
                </Link>
                <AppButton
                  data-testid={`projects-table-delete-project-${project.id}`}
                  appearance="text"
                  size="icon"
                  onClick={() => setIsConfirmationModalOpen(project.id)}
                >
                  <Trash />
                </AppButton>
                <ConfirmationModal
                  isOpen={isConfirmationModalOpen === project.id}
                  onClose={() => setIsConfirmationModalOpen(null)}
                  onConfirm={() => handleArchiveProject(String(project.id))}
                  title="Delete Project"
                  description="Are you sure you want to delete this project?"
                />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};
